/**
 * Authored “find the fault” scenarios — pure TypeScript (no Three.js).
 *
 * Each fault is the healthy baseline minus one (or two) open jumpers.
 * Learner-facing copy must never name the active id. HINT text may name a
 * visible device (fan, sign, switch) conceptually — never the jumper to reseat.
 */

import {
  HEALTHY_ROOM_CONNECTIONS,
  HEALTHY_ROOM_SWITCHES,
  type RoomDeviceId,
  type RoomOutputId,
  type RoomSwitchId,
} from "./roomCircuit";

export const ROOM_FAULT_IDS = [
  "open-light-a",
  "open-fan",
  "open-sign",
  "open-light-b-branch",
  "open-outlet",
  "open-utility-upstream",
] as const;

export type RoomFaultId = (typeof ROOM_FAULT_IDS)[number];

export interface RoomFault {
  readonly id: RoomFaultId;
  readonly connections: ReadonlyArray<readonly [string, string]>;
  readonly switches: Readonly<Record<RoomSwitchId, boolean>>;
  /** Conceptual only — never the answer / never the fault id. */
  readonly hint?: string;
  readonly affectedDeviceIds: readonly RoomDeviceId[];
}

function omitOutputs(
  open: readonly RoomOutputId[],
): ReadonlyArray<readonly [string, string]> {
  const skip = new Set<string>(open);
  return HEALTHY_ROOM_CONNECTIONS.filter((pair) => !skip.has(pair[1]));
}

const HEALTHY_SWITCHES: Readonly<Record<RoomSwitchId, boolean>> = {
  ...HEALTHY_ROOM_SWITCHES,
};

export const ROOM_FAULTS: readonly RoomFault[] = [
  {
    id: "open-light-a",
    connections: omitOutputs(["outLightA"]),
    switches: HEALTHY_SWITCHES,
    hint: "Check whether power reaches the lighting branch.",
    affectedDeviceIds: ["ceilingLightA"],
  },
  {
    id: "open-fan",
    connections: omitOutputs(["outFan"]),
    switches: HEALTHY_SWITCHES,
    hint: "Check whether power reaches the fan branch.",
    affectedDeviceIds: ["fan"],
  },
  {
    id: "open-sign",
    connections: omitOutputs(["outSign"]),
    switches: HEALTHY_SWITCHES,
    hint: "Check whether power reaches the sign branch.",
    affectedDeviceIds: ["sign"],
  },
  {
    id: "open-light-b-branch",
    connections: omitOutputs(["outLightB"]),
    switches: HEALTHY_SWITCHES,
    hint: "Try testing before and after the switch.",
    affectedDeviceIds: ["ceilingLightB"],
  },
  {
    id: "open-outlet",
    connections: omitOutputs(["outOutlet"]),
    switches: HEALTHY_SWITCHES,
    hint: "Check whether power reaches the outlet branch.",
    affectedDeviceIds: ["outlet"],
  },
  {
    id: "open-utility-upstream",
    connections: omitOutputs(["outFan", "outOutlet"]),
    switches: HEALTHY_SWITCHES,
    hint: "More than one device is affected.",
    affectedDeviceIds: ["fan", "outlet"],
  },
];

const FAULT_BY_ID: ReadonlyMap<RoomFaultId, RoomFault> = new Map(
  ROOM_FAULTS.map((fault) => [fault.id, fault]),
);

export function isRoomFaultId(id: string): id is RoomFaultId {
  return FAULT_BY_ID.has(id as RoomFaultId);
}

export function getRoomFault(id: string): RoomFault | undefined {
  if (!isRoomFaultId(id)) return undefined;
  return FAULT_BY_ID.get(id);
}

/**
 * Pick a random authored fault, skipping `excludeId` when another choice exists.
 */
export function pickRoomFault(excludeId?: string | null): RoomFault {
  const pool =
    excludeId && ROOM_FAULTS.length > 1
      ? ROOM_FAULTS.filter((fault) => fault.id !== excludeId)
      : ROOM_FAULTS;
  const list = pool.length > 0 ? pool : ROOM_FAULTS;
  const index = Math.floor(Math.random() * list.length);
  return list[index]!;
}

/** First authored scenario — used at construct so load is never fully powered. */
export const FIRST_ROOM_FAULT: RoomFault = ROOM_FAULTS[0]!;
